import {
  DealerMarkupsDto,
  DealerMarkupsFilterDto,
  DealerMarkupItemDto,
  DealerPublicationQuantityDto,
  DealerRemainsSOItemDto,
  DealerRemainsVisibilityDto,
} from "../api/types";
import {
  FilterOption,
  MarkupRule,
  StockPublicationDictionaries,
  StockPublicationSettings,
} from "../types";

export type VisibilitySettingsSlice = Pick<
  StockPublicationSettings,
  "hiddenBrands" | "hiddenModels" | "hiddenSizes" | "hiddenDrillings"
>;

export type QuantitySettingsSlice = Pick<
  StockPublicationSettings,
  "maxVisibleQuantity" | "hideLastFullSet"
>;

const itemToFilterOption = (name: string): FilterOption => ({
  id: name,
  name,
});

const mapItemsToOptions = (
  items: DealerRemainsSOItemDto[] = [],
): FilterOption[] => items.map((item) => itemToFilterOption(item.name));

const mapSelectedNames = (items: DealerRemainsSOItemDto[] = []): string[] =>
  items.filter((item) => item.isSelected).map((item) => item.name);

const mapOptionsToApiItems = (
  options: FilterOption[],
  selectedIds: string[],
): DealerRemainsSOItemDto[] => {
  const selectedSet = new Set(selectedIds);
  return options.map((option) => ({
    name: option.name,
    isSelected: selectedSet.has(option.id),
  }));
};

export const buildVisibilityDictionaries = (
  dto: DealerRemainsVisibilityDto,
): Pick<
  StockPublicationDictionaries,
  "brands" | "models" | "sizes" | "drillings"
> => ({
  brands: mapItemsToOptions(dto.restrictedBrand),
  models: mapItemsToOptions(dto.restrictedDesign),
  sizes: mapItemsToOptions(dto.restrictedDiameter),
  drillings: mapItemsToOptions(dto.restrictedDrilling),
});

export const mapVisibilitySelectionFromApi = (
  dto: DealerRemainsVisibilityDto,
): VisibilitySettingsSlice => ({
  hiddenBrands: mapSelectedNames(dto.restrictedBrand),
  hiddenModels: mapSelectedNames(dto.restrictedDesign),
  hiddenSizes: mapSelectedNames(dto.restrictedDiameter),
  hiddenDrillings: mapSelectedNames(dto.restrictedDrilling),
});

export const mapVisibilityToApi = (
  settings: VisibilitySettingsSlice,
  dictionaries: Pick<
    StockPublicationDictionaries,
    "brands" | "models" | "sizes" | "drillings"
  >,
): DealerRemainsVisibilityDto => ({
  restrictedBrand: mapOptionsToApiItems(
    dictionaries.brands,
    settings.hiddenBrands,
  ),
  restrictedDesign: mapOptionsToApiItems(
    dictionaries.models,
    settings.hiddenModels,
  ),
  restrictedDrilling: mapOptionsToApiItems(
    dictionaries.drillings,
    settings.hiddenDrillings,
  ),
  restrictedDiameter: mapOptionsToApiItems(
    dictionaries.sizes,
    settings.hiddenSizes,
  ),
});

export const mapQuantityFromApi = (
  dto: DealerPublicationQuantityDto,
): QuantitySettingsSlice => ({
  maxVisibleQuantity:
    dto.maxQuantityToShow != null && dto.maxQuantityToShow > 0
      ? dto.maxQuantityToShow
      : null,
  hideLastFullSet: !dto.showLastSet,
});

export const mapQuantityToApi = (
  settings: QuantitySettingsSlice,
): DealerPublicationQuantityDto => ({
  maxQuantityToShow: settings.maxVisibleQuantity,
  showLastSet: !settings.hideLastFullSet,
});

export const buildMarkupDictionaries = (
  dto: DealerMarkupsFilterDto,
): Pick<StockPublicationDictionaries, "markupBrands" | "markupDiameters"> => ({
  markupBrands: dto.brands.map((brand) => ({
    id: String(brand.brandId),
    name: brand.brandName,
  })),
  markupDiameters: dto.diameters.map((diameter) => ({
    id: String(diameter),
    name: String(diameter),
  })),
});

export const buildDictionariesFromApi = (
  visibility: DealerRemainsVisibilityDto,
  markupFilters: DealerMarkupsFilterDto,
): StockPublicationDictionaries => ({
  ...buildVisibilityDictionaries(visibility),
  ...buildMarkupDictionaries(markupFilters),
});

export const mapMarkupsFromApi = (dto: DealerMarkupsDto): MarkupRule[] =>
  (dto.markups ?? []).map((item, index) => ({
    id: `rule-${item.brandId ?? "b"}-${item.diameter ?? "d"}-${index}`,
    brandId: item.brandId != null ? String(item.brandId) : "",
    diameterId: item.diameter != null ? String(item.diameter) : "",
    percent: Number(item.markup),
  }));

export const mapMarkupsToApi = (
  markups: MarkupRule[],
  dictionaries: Pick<StockPublicationDictionaries, "markupBrands">,
): DealerMarkupItemDto[] =>
  markups.map((rule) => {
    const brand = dictionaries.markupBrands.find((b) => b.id === rule.brandId);
    const brandId = Number.parseInt(rule.brandId, 10);

    return {
      brandId: Number.isNaN(brandId) ? null : brandId,
      brandName: brand?.name ?? null,
      diameter: Number.parseFloat(rule.diameterId) || null,
      markup: rule.percent,
    };
  });

export const buildSettingsFromApi = (
  visibility: DealerRemainsVisibilityDto,
  quantity: DealerPublicationQuantityDto,
  markups: DealerMarkupsDto,
): StockPublicationSettings => ({
  ...mapVisibilitySelectionFromApi(visibility),
  ...mapQuantityFromApi(quantity),
  markups: mapMarkupsFromApi(markups),
});
